import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { motion } from "framer-motion";

import { api } from "@/lib/api";

export default function MiniLandingPage() {
    const { slug } = useParams();
    const [params] = useSearchParams();
    const preview = params.get("preview") === "true";
    const [row, setRow] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const r = await api.miniLandingGetPublic(slug, preview);
                if (mounted) setRow(r);
            } catch (e) {
                if (mounted) setErr(e.message || "Landing not found");
            }
        })();
        return () => { mounted = false; };
    }, [slug, preview]);

    const isPreview = preview && row?.status !== "published";

    const onCtaClick = () => {
        // Fire-and-forget beacon; navigation is preserved by anchor default action.
        api.miniLandingCtaClick(slug).catch(() => {});
    };

    if (err) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex items-center justify-center p-8">
                <div className="max-w-md text-center">
                    <h1 className="text-2xl font-semibold">Landing not found</h1>
                    <p className="text-text-secondary mt-2 text-sm">
                        This landing may have been unpublished or the URL is incorrect.
                    </p>
                    <a href="/" className="mt-6 inline-block text-sm text-[var(--color-secondary)]">
                        ← Back to home
                    </a>
                </div>
            </div>
        );
    }
    if (!row) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)]" />
        );
    }
    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main" data-testid="mini-landing-public">
            {isPreview && (
                <div className="w-full bg-neutral-400/10 border-b border-neutral-400/40 text-neutral-400 text-xs text-center py-2"
                     data-testid="mini-landing-preview-banner">
                    Draft preview · not indexable · status: {row.status}
                </div>
            )}
            <main className="max-w-3xl mx-auto px-6 py-16 md:py-24">
                <motion.header
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="mb-10"
                >
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight text-text-main"
                        data-testid="mini-landing-hero-headline">
                        {row.hero_headline}
                    </h1>
                    {row.hero_subheadline && (
                        <p className="mt-4 text-lg text-text-secondary max-w-2xl"
                            data-testid="mini-landing-hero-subheadline">
                            {row.hero_subheadline}
                        </p>
                    )}
                    <a href={row.cta_url || "/"}
                       onClick={onCtaClick}
                       data-testid="mini-landing-cta"
                       className="inline-flex mt-8 rounded-full bg-white hover:bg-[#EDEDED] text-black px-6 py-3 text-sm font-medium">
                        {row.cta_text || "Get started"}
                    </a>
                </motion.header>

                {row.body_markdown && (
                    <motion.article
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.4 }}
                        className="prose prose-invert max-w-none prose-headings:text-text-main prose-a:text-[var(--color-secondary)]"
                        data-testid="mini-landing-body"
                    >
                        <ReactMarkdown>{row.body_markdown}</ReactMarkdown>
                    </motion.article>
                )}

                <div className="mt-16 pt-8 border-t border-[var(--color-border)]/40 text-xs text-text-secondary">
                    <div className="flex items-center justify-between">
                        <span>Template · {row.template_used}</span>
                        <span>Language · {row.target_language}</span>
                    </div>
                </div>
            </main>
        </div>
    );
}
